import { useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import BigTableLayout, {
  useBigTableLayoutNew,
} from 'components/Layout/BigTableLayout';
import BigTable from 'components/Tables/Big';
import { TRow } from 'components/Kizen/Table';
import TablePagination from 'components/Tables/Big/TablePagination';
import PageToolbar, {
  PageToolbarSection,
  PageToolbarTitle,
} from 'components/Layout/PageToolbar';
import Button from 'components/Button';
import ButtonWithDropdown from 'components/Button/WithDropdown';
import { getTemplatesColumns } from './columns';
import { useTranslation } from 'react-i18next';
import { PageSearchInput } from 'components/Layout/PageToolbar';
import { useNavBarState } from 'app/navBarController';
import { useSelector } from 'react-redux';
import { selectLibraryTemplatesFiltering } from 'store/libraryPage/library.redux';
import { SUB_NAVIGATION_HEIGHT } from 'pages/Library/helpers';
import { AiChatModal } from 'ts-components/AiChatModal';
import { FLOW_TYPE, TASK_TYPE, FLOW_STATUS } from 'ts-components/Conversation';
import useModal from 'components/Modals/useModal';
import { useToast, toastVariant } from 'components/ToastProvider';

const DesktopLayout = ({
  search,
  onChangeSearch,
  templates,
  handleChangeSearch,
  templatesWithoutMetaCount,
  pageNumber,
  pageSize,
  handleChangePageNumber,
  handleChangePageSize,
  sort,
  handleChangeSort,
  handleSelectAction,
  handleClickNewTemplate,
  handleUpdateTemplate,
  timezone,
  ...others
}) => {
  const { height } = useNavBarState();
  const [showToast] = useToast();
  const { filterDropdownRef, scrollTransitioned, ...bigTablePageProps } =
    useBigTableLayoutNew(height, height + SUB_NAVIGATION_HEIGHT);
  const { t } = useTranslation();
  const business = useSelector((s) => s.authentication.chosenBusiness);
  const hasAiPreview = business?.entitlements?.ai_preview;
  const history = useHistory();

  const [aiChatModalProps, , aiChatModal] = useModal({
    handleSubmit: () => aiChatModal.hide(),
  });

  const { scrolledToTable } = bigTablePageProps;
  const templatesHeadData = useMemo(
    () => ({
      meta: {
        sort,
        onSort: (column, direction) => handleChangeSort({ column, direction }),
      },
    }),
    [sort, handleChangeSort]
  );
  const templatesColumns = useMemo(
    () =>
      getTemplatesColumns({
        onUpdateTemplate: handleUpdateTemplate,
        onSelectAction: handleSelectAction,
        timezone,
        t,
      }),
    [handleSelectAction, handleUpdateTemplate, timezone, t]
  );

  const handleAiChatComplete = useCallback(
    ({ run_metadata = {} } = {}) => {
      aiChatModal.hide();
      const generatedEmailTemplate = Object.values(
        run_metadata?.email_templates
      )[0];
      if (
        !run_metadata ||
        generatedEmailTemplate?.status === FLOW_STATUS.FAILED ||
        !generatedEmailTemplate.url
      ) {
        showToast({
          message: t(
            'Failed to generate email template, please try again later'
          ),
          variant: toastVariant.FAILURE,
        });
      } else {
        showToast({
          message: t('Email created successfully in Kizen AI Lab'),
          variant: toastVariant.SUCCESS,
        });

        const relativeUrl = new URL(
          generatedEmailTemplate.url,
          window.location.origin
        ).pathname;
        history.push(relativeUrl);
      }
    },
    [aiChatModal, showToast, history, t]
  );

  const isFiltering = useSelector(selectLibraryTemplatesFiltering);

  return (
    <>
      <BigTableLayout
        toolbar={
          <PageToolbar>
            <PageToolbarSection>
              <PageSearchInput
                placeholder={t('Find Templates')}
                value={search}
                onChange={onChangeSearch}
                loading={isFiltering}
                isClearable
              />
            </PageToolbarSection>
            <PageToolbarTitle
              count={templatesWithoutMetaCount}
              single={t('Template')}
              plural={t('Templates')}
            />
            <PageToolbarSection>
              {hasAiPreview ? (
                <ButtonWithDropdown
                  color="green"
                  onChange={(option) => {
                    switch (option.value) {
                      case 'aiChat':
                        aiChatModal.show();
                        break;
                      default:
                        break;
                    }
                  }}
                  onClick={handleClickNewTemplate}
                  options={[{ value: 'aiChat', label: t('Create with AI') }]}
                >
                  {t('New Template')}
                </ButtonWithDropdown>
              ) : (
                <Button onClick={handleClickNewTemplate}>
                  {t('New Template')}
                </Button>
              )}
            </PageToolbarSection>
          </PageToolbar>
        }
        pagination={
          <TablePagination
            page={pageNumber}
            perPage={pageSize}
            totalCount={templatesWithoutMetaCount}
            onChangePage={handleChangePageNumber}
            onChangePerPage={handleChangePageSize}
          />
        }
        {...bigTablePageProps}
        {...others}
      >
        <BigTable
          scrolledToTable={scrolledToTable}
          columns={templatesColumns}
          head={
            <TRow head columns={templatesColumns} data={templatesHeadData} />
          }
          staleData={isFiltering}
        >
          {templates.map((item) => (
            <TRow key={item.id} columns={templatesColumns} data={item} />
          ))}
        </BigTable>
      </BigTableLayout>
      <AiChatModal
        {...aiChatModalProps}
        entityName={t('Email')}
        heading={t('Create an Email in Kizen AI Lab')}
        context={{}}
        flowType={FLOW_TYPE.EMAIL_BUILDER}
        taskType={TASK_TYPE.EMAIL_PLAN_GENERATION}
        successMessage={t('Email created successfully in Kizen AI Lab')}
        onConfirm={handleAiChatComplete}
        onError={() => aiChatModal.hide()}
        pendingMessages={[
          t('Analyzing your requirements'),
          t("Updating your email's format"),
          t('Refining fields needed for your email'),
          t('Finalizing setup'),
        ]}
      />
    </>
  );
};

export default DesktopLayout;
